import isEqual from 'lodash/isEqual';

import {
  BehaviorShowClause,
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsSeriesTree,
} from 'common/report/insights/util/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {Report} from 'common/report/insights/models/report';
import {isEmptyRetentionResult} from 'common/report/insights/util';
import {BirthIntervalUnit, RetentionAlignmentType, RetentionType} from 'common/report/retention/util/types';
import {getUiRowsDataFromParsed} from 'common/report/retention/util/parsing';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {ChartType, RETENTION_MATH_TYPES} from 'common/report/constants';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {BookmarkDateRange, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SortBy, SortConfig, SortOrder} from 'common/report/util/sorting/types';
import {getEventDisplayName, getRetentionBucketName} from 'common/report/retention/util';
import {getDefaultSortConfig} from 'common/report/util/sorting/table-sorting';
import {RetentionBookmarkType} from 'common/report/retention/charts/types';
import {flattenSegments} from 'common/report/retention/util/request';
import {RetentionData, SegmentKeyToPath} from 'common/report/retention/models/types';
import {getDataGroupName} from 'common/report/retention/util/format';
import {RetentionPopover} from 'common/report/retention/visualization/popover';
import {formatMetricNumber, formatPercent} from 'common/util';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';

import {BookmarkController} from './index';

export class MultimetricBookmarkController implements BookmarkController {
  type = RetentionBookmarkType.MULTIMETRIC;
  bookmark: InsightsBookmarkParams;
  queryResults: InsightsQueryResults;

  constructor(bookmark: InsightsBookmarkParams, results: InsightsQueryResults) {
    this.bookmark = bookmark;
    this.queryResults = results;
  }

  public hasResults(): boolean {
    return !isEmptyRetentionResult(this.queryResults);
  }

  public getTimeSeries(path: string[]): InsightsSeriesTree {
    let timeSeries: InsightsSeriesTree = this.queryResults.series;
    path.forEach((seg) => {
      timeSeries = timeSeries[seg] as InsightsSeriesTree;
    });
    return timeSeries;
  }

  public getChartType(): ChartType {
    return this.bookmark.displayOptions.chartType;
  }

  public getLegendSelectedSegments(): null | string[] {
    const individualSelectedSegments = this.bookmark.legend?.individualSelectedSegments;
    if (!individualSelectedSegments) return null;

    return (
      individualSelectedSegments &&
      individualSelectedSegments.selected &&
      individualSelectedSegments.selected.map((item) => item.key)
    );
  }

  public getLegendExpandedSegments(): string[] {
    const individualSelectedSegments = this.bookmark.legend?.individualSelectedSegments;
    if (!individualSelectedSegments) return [];

    return (
      (individualSelectedSegments &&
        individualSelectedSegments.expanded &&
        individualSelectedSegments.expanded.map((item) => item.key)) ||
      []
    );
  }

  public isPercentageView(): boolean {
    const measurement = (this.bookmark.sections.show[0] as BehaviorShowClause).measurement;
    return measurement.math === RETENTION_MATH_TYPES.RETENTION_RATE;
  }

  public isCumulative(): boolean {
    const showClause = this.bookmark.sections.show[0] as BehaviorShowClause;
    return showClause.measurement.retentionCumulative;
  }

  public isUnbounded(): boolean {
    return (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.retentionUnbounded;
  }

  public getLiftComparison(): LiftComparison {
    return this.bookmark.liftComparison;
  }

  public getSorting(): SortConfig {
    const segmentationLevel = this.getBreakdownCount();
    const sortConfig = this.bookmark.sorting[ChartType.RetentionCurve];
    return (
      sortConfig ||
      getDefaultSortConfig({
        numSegmentColumns: segmentationLevel || 1,
        valueSortField: `size`,
        flatSortConfig: {
          sortBy: SortBy.Value,
          sortOrder: SortOrder.Descending,
        },
      })
    );
  }

  public getBornEvent(): string {
    const behaviors = (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.behaviors || [];
    return behaviors[0]?.name || ``;
  }
  public getEvent(): string {
    const behaviors = (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.behaviors || [];
    return behaviors[1]?.name || ``;
  }
  public getRetentionAlignmentType(): RetentionAlignmentType {
    return (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.retentionAlignmentType;
  }

  public getSegments(): Segment[] {
    return getSelectedSegments(this.getBookmarkAsReport().sections.group.clauses);
  }

  public getRetentionType(): RetentionType {
    return (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.retentionType;
  }
  public getRetentionUnit(): TimeUnit {
    return (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.retentionUnit;
  }
  public getDateUnit(): BirthIntervalUnit {
    return this.bookmark.sections.time[0].unit;
  }
  public getDateRange(): BookmarkDateRange {
    const time = this.queryResults.date_range;
    const dateRange = new DateRange({
      type: DateRangeType.Between,
      from: time.from_date,
      to: time.to_date,
    });
    return dateRange.getBookmarkFormat();
  }
  public getBucketSizes(): number[] {
    return (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.retentionCustomBucketSizes;
  }

  public getDataGroupId() {
    return (this.bookmark.sections.show[0] as BehaviorShowClause).behavior.dataGroupId;
  }
  public getDataGroupName(plural = false): string {
    return getDataGroupName(this.getDataGroupId(), plural);
  }
  public getRetentionBucketIndex(): number {
    return (this.bookmark.sections.show[0] as BehaviorShowClause).measurement.retentionBucketIndex;
  }

  public getIncompletBuckets() {
    return this.queryResults.meta.report_sections.show[0].incomplete_buckets;
  }

  public createPopover({
    projectTimezone,
    colIdx,
    rowIdx,
    groupedRowIdx = null,
    absoluteValue,
    absoluteDropOffValue,
    cohortSize,
    completeDate,
    enteredValue,
    enteredDate,
    incomplete,
    isAggregated,
  }: {
    projectTimezone: string;
    colIdx: number;
    rowIdx: number;
    groupedRowIdx?: number;
    absoluteValue: string;
    absoluteDropOffValue: string;
    cohortSize: number;
    completeDate: string | false;
    enteredValue: string;
    enteredDate?: Date;
    incomplete: boolean;
    isAggregated: boolean;
  }): RetentionPopover {
    return new RetentionPopover(
      {
        bornEventName: this.getBornEvent() && getEventDisplayName(this.getBornEvent()),
        bucketSizes: this.getBucketSizes(),
        dataGroupName: this.getDataGroupName(false),
        dataGroupNamePlural: this.getDataGroupName(true),
        eventName: getEventDisplayName(this.getEvent()),
        groupBy: this.getSegments(),
        retentionUnit: this.getRetentionUnit(),
        dateUnit: this.getDateUnit(),
        dateRange: this.getDateRange(),
        retentionType: this.getRetentionType(),
        retentionAlignmentType: this.getRetentionAlignmentType(),
        unbounded: this.isUnbounded(),
        isCumulative: this.isCumulative(),
        chartType: this.getChartType(),
        projectTimezone,
        metricLabel: this.getMetricLabel(),
        bookmark: this.bookmark,
      },
      {
        absoluteValue,
        absoluteDropOffValue,
        cohortSize,
        columnIdx: this.getChartType() === ChartType.Line ? this.getRetentionBucketIndex() : colIdx, // bucket index i.e. < 1 Day, Day 1, Day 2, ...
        rowIdx,
        ...(groupedRowIdx !== null && {groupedRowIdx}), // interval index i.e. Aug 15, 2022, Aug 22, 2022, ...
        completeDate,
        enteredValue,
        enteredDate,
        incomplete,
        isAggregated,
      },
    );
  }

  isSegmented() {
    return this.bookmark.sections.group.length > 0;
  }

  private getBreakdownCount() {
    return this.bookmark.sections.group.length;
  }

  getBookmarkAsReport() {
    // Utility to get the bookmark in report form (which has some serialization done on it)
    return Report.fromBookmark({params: this.bookmark, type: BookmarkType.Insights, name: ``, description: ``});
  }

  flattenData({
    queryResults,
    segments,
  }: {
    queryResults: InsightsQueryResults;
    segments: Segment[];
  }): {
    segmentKeyToPath: SegmentKeyToPath;
    data: RetentionData;
    compareToPastData?: RetentionData;
    liftData?: RetentionData;
  } {
    const results = Object.values(queryResults.series)[0];
    const pastResults = queryResults.time_comparison && Object.values(queryResults.time_comparison.series)[0];
    const liftResults = queryResults.lift_comparison && Object.values(queryResults.lift_comparison.series)[0];
    if (this.isSegmented()) {
      const {responseData: data, segmentKeyToPath} = flattenSegments(results, segments);
      const compareToPastData = pastResults && flattenSegments(pastResults, segments).responseData;
      const liftData = liftResults && flattenSegments(liftResults, segments).responseData;
      return {
        segmentKeyToPath,
        data,
        ...(compareToPastData && {compareToPastData}),
        ...(liftData && {liftData}),
      };
    }
    return {
      segmentKeyToPath: null,
      data: results,
      ...(pastResults && {compareToPastData: pastResults}),
      ...(liftResults && {liftData: liftResults}),
    };
  }

  private getMetricLabel(): string {
    const series = this.queryResults.series;
    const metricLabel = Object.keys(series)[0];
    return metricLabel;
  }

  public getCurveChartData(timezone: string) {
    const {segmentKeyToPath, data, compareToPastData, liftData} = this.flattenData({
      queryResults: this.queryResults,
      segments: this.getSegments(),
    });
    const incompleteBuckets = this.queryResults.meta.report_sections.show[0].incomplete_buckets;
    const pastIncompleteBuckets = this.queryResults.meta.report_sections.show[0].past_incomplete_buckets;
    const {formattedUiRows, formattedLiftRowsData} = getUiRowsDataFromParsed({
      data,
      incompleteBuckets,
      bookmarkParams: this.bookmark,
      timezone,
      compareToPastData,
      pastIncompleteBuckets,
      liftData,
    });

    return {chartRows: formattedUiRows, formattedLiftRowsData, segmentKeyToPath};
  }

  percentLabel(value: number) {
    return formatPercent(value, 0);
  }

  absoluteLabel(value: number) {
    return formatMetricNumber(value);
  }

  public percentOrAbsoluteLabel() {
    if (this.getLiftComparison()) {
      return this.percentLabel;
    }
    return this.isPercentageView() ? this.percentLabel : this.absoluteLabel;
  }

  public bucketLabeler(): (number: number) => string {
    const retentionType = this.getRetentionType();
    const retentionUnit = this.getRetentionUnit();
    const dateUnit = this.getDateUnit();
    const bucketSizes = this.getBucketSizes();
    return function (value: number) {
      return getRetentionBucketName({
        bucketIndex: value,
        retentionType,
        retentionUnit,
        dateUnit,
        bucketSizes,
      });
    };
  }

  public isEquivalent(bookmark: InsightsBookmarkParams, results: InsightsQueryResults) {
    return isEqual(this.bookmark, bookmark) && isEqual(this.queryResults, results);
  }
}
